import gameIframe from '@ps/bridge/iframe/iframe'
import { removeUnSplitProInLayout } from '@ps/bridge/qc/node/layout'
import { scripts } from '@ps/bridge/qc/scripts'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { nodeTypes } from '@ps/bridge/qc/scripts/editor'
import { EventBus, Event } from '@ps/utils/event-bus'
import {
  cookPlayPuzzleData,
  getEmptyFilters,
  getPuzzleBoardRowCol,
  getPuzzleNode,
  refreshPuzzlePieces,
} from '@ps/workbench/undo-redo/module/puzzle'
import _ from 'lodash'

const getInitState = () => {
  return {
    dialog: {
      visible: false,
      appendToBody: true,
      modal: true,
      width: '800px',
      top: '88px',
      uuid: '',
      onReplaced: () => {},
    },
    bgSettingDialog: {
      visible: false,
      appendToBody: true,
      modal: false,
      width: '320px',
      top: '128px',
      right: 'calc(648px - 100vw)',
    },
    step: 1,
    scale: 1, // 编辑画布区缩放大小
    sceneNode: null, // 打开编辑面板对应的场景节点
    backupData: null, // 备份数据，用于直接关闭弹窗不保存时还原原来的数据
    historyList: [],
    undoList: [],
    isSelectTool: false,
    isEraseTool: false,
    resourceSelected: null, // 选中的资源
    puzzlePieces: [], // 切出来的拼图小块数组
    bgImage: null, // 底图
    piecesNum: 0, // 块数
    shapeType: 0, // 形状
    borderType: 0, // 描边
    borderAdvancedSet: null, // 高级设置
    playPuzzle: [], // 第二步的特定玩法拼图块数据
  }
}

const puzzle = {
  namespaced: true,

  state: getInitState(),

  mutations: {
    openDialog(state) {
      state.dialog.visible = true
      state.scale = 1
      state.step = 1
      state.resourceSelected = null
      state.isSelectTool = false
      state.isEraseTool = false
      // 读取脚本中的数据
      const puzzleNode = getPuzzleNode(state.sceneNode)
      const puzzleComponent = puzzleNode.getScript(scripts.puzzle.name)
      if (puzzleComponent.psConfig) {
        state.bgImage = _.cloneDeep(puzzleComponent.psConfig.bgImage)
        if (state.bgImage) {
          puzzleComponent.deploy()
          refreshPuzzlePieces()
        }
      } else {
        state.bgImage = null
      }
      state.piecesNum = puzzleComponent.piecesNum
      state.shapeType = puzzleComponent.shapeType
      state.borderType = puzzleComponent.borderType
      state.borderAdvancedSet = puzzleComponent.borderAdvancedSet
      state.playPuzzle = [
        ...Object.values(puzzleComponent.puzzlePiecesGroupSet),
      ]
      // 备份数据
      state.backupData = {
        bgImage: _.cloneDeep(state.bgImage),
        piecesNum: state.piecesNum,
        shapeType: state.shapeType,
        borderType: state.borderType,
        borderAdvancedSet: _.cloneDeep(state.borderAdvancedSet),
        puzzlePiecesGroupSet: _.cloneDeep(state.playPuzzle),
      }
    },
    closeDialog(state) {
      state.dialog.visible = false
      state.puzzlePieces = []
      state.resourceSelected = null
      state.step = 0
    },
    updateIsEraseTool(state, value) {
      state.isEraseTool = value
    },
    updateIsSelectTool(state, value) {
      state.isSelectTool = value
      // 没有选中资源时，默认用第一个
      if (!state.resourceSelected) {
        state.resourceSelected = _.cloneDeep(state.playPuzzle[0])
      }
    },
    updateSceneNode(state, value) {
      state.sceneNode = value
    },
    updateStep(state, value) {
      state.step = value
    },
    updateBgImage(state, value) {
      if (value) {
        if (!state.bgImage) {
          state.bgImage = {}
        }
        const ext = {
          filters: getEmptyFilters(),
          oriImageUrl: [value.imageUrl],
        }
        state.bgImage = Object.assign(state.bgImage, value, ext)
        state.bgImage = _.cloneDeep(state.bgImage)
      } else {
        state.bgImage = value
      }
      const puzzleNode = getPuzzleNode(state.sceneNode)
      const puzzleComponent = puzzleNode.getScript(scripts.puzzle.name)
      if (!puzzleComponent.psConfig) {
        puzzleComponent.psConfig = {}
      }
      puzzleComponent.psConfig = Object.assign(puzzleComponent.psConfig, {
        bgImage: state.bgImage,
      })
    },
    updateScale(state, value) {
      state.scale = value
    },
    updateBgProps(state, data) {
      state.bgImage = Object.assign(state.bgImage, data)
      state.bgImage = _.cloneDeep(state.bgImage)
    },
    updateResourceSelected(state, value) {
      state.resourceSelected = value
    },
    openBgSettingDialog(state) {
      state.bgSettingDialog.visible = true
    },
    closeBgSettingDialog(state) {
      state.bgSettingDialog.visible = false
    },
    updatePuzzlePiece(state, value) {
      state.puzzlePieces = value
    },
    updatePiecesNum(state, value) {
      state.piecesNum = value
      const puzzleNode = getPuzzleNode(state.sceneNode)
      const puzzleComponent = puzzleNode.getScript(scripts.puzzle.name)
      puzzleComponent.piecesNum = value
    },
    updateShapeType(state, value) {
      state.shapeType = value
      const puzzleNode = getPuzzleNode(state.sceneNode)
      const puzzleComponent = puzzleNode.getScript(scripts.puzzle.name)
      puzzleComponent.shapeType = value
    },
    updateBorderType(state, value) {
      state.borderType = value
      const puzzleNode = getPuzzleNode(state.sceneNode)
      const puzzleComponent = puzzleNode.getScript(scripts.puzzle.name)
      puzzleComponent.borderType = value
    },
    updateBorderAdvancedSet(state, value) {
      state.borderAdvancedSet = _.cloneDeep(value)
      const puzzleNode = getPuzzleNode(state.sceneNode)
      const puzzleComponent = puzzleNode.getScript(scripts.puzzle.name)
      puzzleComponent.borderAdvancedSet = _.cloneDeep(state.borderAdvancedSet)
    },
    addPlayPuzzle(state, value) {
      state.resourceSelected = cookPlayPuzzleData(value)
      state.isSelectTool = true
      state.isEraseTool = false
      state.playPuzzle.push(value)
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    deletePlayPuzzle(state, id) {
      if (state.resourceSelected && id === state.resourceSelected.id) {
        state.resourceSelected = null
        state.isSelectTool = false
      }
      state.playPuzzle = state.playPuzzle.filter((item) => item.id !== id)
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    updatePlayPuzzle(state, data) {
      const index = state.playPuzzle.findIndex((item) => item.id === data.id)
      if (data.value === null) {
        state.playPuzzle[index][data.key] = null
      } else if (
        Object.prototype.toString.call(data.value) === '[object Object]'
      ) {
        if (!state.playPuzzle[index][data.key]) {
          state.playPuzzle[index][data.key] = {}
        }
        state.playPuzzle[index][data.key] = Object.assign(
          state.playPuzzle[index][data.key],
          data.value,
        )
      } else {
        state.playPuzzle[index][data.key] = data.value
      }
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    previewStyle(state, value) {
      for (let i = 0; i < state.playPuzzle.length; i++) {
        state.playPuzzle[i]['preview'] = value
      }
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    addGridPlayPuzzle(state, data) {
      const index = state.playPuzzle.findIndex((item) => item.id === data.id)
      for (let i = 0; i < state.playPuzzle.length; i++) {
        // eslint-disable-next-line no-continue
        if (i === index) continue
        const index2 = state.playPuzzle[i]['posArr'].findIndex(
          (item) => item.row === data.row && item.col === data.col,
        )
        if (index2 > -1) {
          state.playPuzzle[i]['posArr'].splice(index2, 1)
        }
      }
      if (!state.playPuzzle[index]['posArr']) {
        state.playPuzzle[index]['posArr'] = []
        state.playPuzzle[index]['posArr'].push({ row: data.row, col: data.col })
      } else {
        if (
          state.playPuzzle[index]['posArr'].some(
            (item) => item.row === data.row && item.col === data.col,
          )
        ) {
          return
        }
        state.playPuzzle[index]['posArr'].push({ row: data.row, col: data.col })
      }
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    removeGridPlayPuzzle(state, data) {
      for (let i = 0; i < state.playPuzzle.length; i++) {
        // eslint-disable-next-line no-continue
        if (!state.playPuzzle[i]['posArr']) continue
        const index = state.playPuzzle[i]['posArr'].findIndex(
          (item) => item.row === data.row && item.col === data.col,
        )
        if (index > -1) {
          state.playPuzzle[i]['posArr'].splice(index, 1)
        }
      }
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    addAllGrid(state) {
      if (!state.resourceSelected) return
      for (let i = 0; i < state.playPuzzle.length; i++) {
        state.playPuzzle[i]['posArr'] = []
      }
      const index = state.playPuzzle.findIndex(
        (item) => item.id === state.resourceSelected.id,
      )
      const size = getPuzzleBoardRowCol()
      for (let i = 0; i < size.row; i++) {
        for (let j = 0; j < size.col; j++) {
          if (
            !state.playPuzzle[index]['posArr'].some(
              (item) => item.row === i && item.col === j,
            )
          ) {
            state.playPuzzle[index]['posArr'].push({ row: i, col: j })
          }
        }
      }
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    clearAllGrid(state) {
      for (let i = 0; i < state.playPuzzle.length; i++) {
        state.playPuzzle[i]['posArr'] = []
      }
      state.playPuzzle = _.cloneDeep(state.playPuzzle)
      this.dispatch('workspace/puzzle/addHistory', state.playPuzzle)
    },
    emptyPlayPuzzle(state) {
      state.playPuzzle = []
    },
    updatePlayPuzzleByHistory(state, data) {
      state.playPuzzle = data
    },
  },

  actions: {
    addPuzzleNode({ commit, state, dispatch, rootState }, { component }) {
      const currentScene =
        rootState.workspace.game.sceneManager.currentSceneNode
      const puzzleNode = gameIframe.qcGame.add.node()
      const editorData = editorScript.getScript(puzzleNode)
      editorData.$data.nodeType = nodeTypes.puzzle
      puzzleNode.name = 'puzzle'
      puzzleNode.width = 500
      puzzleNode.height = 500
      const puzzleComponent = puzzleNode.addScript(component)
      // 拼图图案节点
      const puzzleImageNode = gameIframe.qcGame.add.image(puzzleNode)
      puzzleImageNode.name = 'puzzleImage'
      puzzleImageNode.visible = false
      editorScript.getScript(puzzleImageNode)
      removeUnSplitProInLayout(puzzleImageNode)
      puzzleComponent.puzzleImg = puzzleImageNode

      currentScene.addChildAt(puzzleNode, currentScene.children.length)
      EventBus.$emit(Event.ADD_GAME_OBJECT, puzzleNode.uuid)
      dispatch(
        'workspace/game/updateActiveNode',
        { node: puzzleNode, record: false },
        {
          root: true,
        },
      )
      this.commit('workspace/game/updateFirstQuickLayoutTip', true)
    },
    initPlayPuzzleHistory({ state }) {
      state.historyList = []
      state.undoList = []
      const cloneData = _.cloneDeep(state.playPuzzle)
      state.historyList.push(cloneData)
    },
    addHistory({ state }, data) {
      const cloneData = _.cloneDeep(data)
      state.historyList.push(cloneData)
      state.undoList = []
    },
    undoHistory({ state, commit }) {
      if (state.historyList.length <= 1) {
        return
      }
      const data = state.historyList.pop()
      state.undoList.push(data)
      const recoverData = state.historyList[state.historyList.length - 1]
      commit('updatePlayPuzzleByHistory', _.cloneDeep(recoverData))
    },
    redoHistory({ state, commit }) {
      if (state.undoList.length === 0) {
        return
      }
      const data = state.undoList.pop()
      state.historyList.push(data)
      const recoverData = state.historyList[state.historyList.length - 1]
      commit('updatePlayPuzzleByHistory', _.cloneDeep(recoverData))
    },
  },
}

export default puzzle
